import type { Circuit, CircuitCellStyle } from '../types/track.js';
import type {
  RaceHistoryPlayerSnapshot,
  RaceHistoryReplay,
  RaceHistoryTurn,
} from '../types/raceHistory.js';
import { buildRaceRanking, buildRaceRankingEntry } from './raceRanking.js';

function mergeCellStyles(
  primary: Record<string, CircuitCellStyle> | undefined,
  secondary: Record<string, CircuitCellStyle> | undefined
): Record<string, CircuitCellStyle> | undefined {
  if (!primary && !secondary) return undefined;
  const merged = { ...secondary, ...primary };
  return Object.keys(merged).length > 0 ? merged : undefined;
}

function attachCircuitVisualFields(out: Circuit, circuit: Circuit): void {
  if (circuit.startLane) out.startLane = circuit.startLane;
  if (circuit.cellStyles && Object.keys(circuit.cellStyles).length > 0) {
    out.cellStyles = circuit.cellStyles;
  }
  if (circuit.decorCells?.length) out.decorCells = circuit.decorCells;
  if (circuit.grandstands?.length) out.grandstands = circuit.grandstands;
  if (circuit.signs?.length) out.signs = circuit.signs;
  if (circuit.vehicles?.length) out.vehicles = circuit.vehicles;
}

/** Circuit normalisé pour affichage jeu + replay (tracé, grille, décor visuel). */
export function normalizeStoredCircuit(circuit: Circuit): Circuit {
  const path = Array.isArray(circuit.path) ? circuit.path : [];
  let width = circuit.width;
  let height = circuit.height;
  if (width < 1 || height < 1) {
    for (const c of path) {
      width = Math.max(width, c.x + 1);
      height = Math.max(height, c.y + 1);
    }
    for (const c of circuit.jokerLap?.cells ?? []) {
      width = Math.max(width, c.x + 1);
      height = Math.max(height, c.y + 1);
    }
    for (const c of circuit.decorCells ?? []) {
      width = Math.max(width, c.x + 1);
      height = Math.max(height, c.y + 1);
    }
  }
  const out: Circuit = {
    path,
    width: Math.max(1, width),
    height: Math.max(1, height),
    grid: circuit.grid?.length ? circuit.grid : [],
  };
  if (circuit.jokerLap?.cells?.length) {
    out.jokerLap = circuit.jokerLap;
  }
  attachCircuitVisualFields(out, circuit);
  return out;
}

/**
 * Fusionne le tracé embarqué dans le replay et le circuit live (BDD / partie).
 * Préserve le tour joker dès qu’il est disponible dans l’une des sources.
 */
export function resolveReplayCircuit(
  embedded: Circuit | undefined,
  live: Circuit | undefined
): Circuit | null {
  const primary = live?.path?.length ? live : embedded;
  if (!primary?.path?.length) return null;
  const secondary = primary === live ? embedded : live;

  const jokerLap = primary.jokerLap?.cells?.length
    ? primary.jokerLap
    : embedded?.jokerLap?.cells?.length
      ? embedded.jokerLap
      : live?.jokerLap;

  const grid = primary.grid?.length
    ? primary.grid
    : embedded?.grid?.length
      ? embedded.grid
      : live?.grid?.length
        ? live.grid
        : [];

  return normalizeStoredCircuit({
    ...primary,
    path: primary.path,
    grid,
    ...(jokerLap?.cells?.length ? { jokerLap } : {}),
    cellStyles: mergeCellStyles(primary.cellStyles, secondary?.cellStyles),
    decorCells: primary.decorCells?.length ? primary.decorCells : secondary?.decorCells,
    startLane: primary.startLane ?? secondary?.startLane,
    grandstands: primary.grandstands?.length ? primary.grandstands : secondary?.grandstands,
    signs: primary.signs?.length ? primary.signs : secondary?.signs,
    vehicles: primary.vehicles?.length ? primary.vehicles : secondary?.vehicles,
  });
}

/** Associe le tracé affiché au replay (nécessaire pour estimer/rejouer les positions). */
export function mergeReplayCircuit(
  replay: RaceHistoryReplay,
  circuit?: Circuit | null
): RaceHistoryReplay {
  const resolved = resolveReplayCircuit(replay.circuit, circuit ?? undefined);
  if (!resolved) return replay;
  return {
    ...replay,
    circuit: resolved,
  };
}

function replayPathLength(replay: RaceHistoryReplay): number {
  return replay.circuit?.path?.length ?? 0;
}

function participantCount(n: number): number {
  return Math.max(2, n);
}

/** `per-roll` : une entrée = un lancer ; `per-entry` : une entrée = tour complet (legacy). */
type ReplayRoundMode = 'per-roll' | 'per-entry';

function rollCountInTurn(turn: RaceHistoryTurn): number {
  return turn.rows.filter((r) => r.roll != null && r.roll > 0).length;
}

function isSequentialRoundEntries(turns: RaceHistoryTurn[]): boolean {
  if (!turns.length) return false;
  const maxTurnNumber = turns.reduce((m, t) => Math.max(m, t.turnNumber ?? 0), 0);
  return turns.every((t, i) => (t.turnNumber ?? i + 1) === i + 1) && maxTurnNumber === turns.length;
}

function detectReplayRoundMode(replay: RaceHistoryReplay, n: number): ReplayRoundMode {
  const turns = replay.turns ?? [];
  if (!turns.length) return 'per-roll';

  /** Une entrée avec plusieurs lancers = tour complet (sims headless incluent actorPlayerIndex). */
  const multiRollEntries = turns.filter((t) => rollCountInTurn(t) >= 2).length;
  if (multiRollEntries >= Math.max(1, Math.ceil(turns.length * 0.5))) return 'per-entry';

  const withActor = turns.filter((t) => t.actorPlayerIndex != null).length;
  if (withActor > turns.length * 0.5) return 'per-roll';

  /** Format actuel : une entrée = un tour de jeu, turnNumber 1…N, sans actorPlayerIndex. */
  if (withActor === 0 && isSequentialRoundEntries(turns)) return 'per-entry';

  const maxTurnNumber = turns.reduce((m, t) => Math.max(m, t.turnNumber ?? 0), 0);
  if (maxTurnNumber === turns.length && maxTurnNumber >= n && withActor > 0) return 'per-roll';

  if (withActor === 0) return 'per-entry';

  return 'per-roll';
}

/**
 * Tour de jeu = chaque joueur a lancé le dé une fois (pas un seul lancer).
 * Dérivé du nombre d’entrées / joueurs (ex. 8 lancers, 2 joueurs → 4 tours).
 */
export function getReplayRoundCount(replay: RaceHistoryReplay, numParticipants: number): number {
  const turns = replay.turns ?? [];
  if (!turns.length) return 0;
  const n = participantCount(numParticipants);
  if (detectReplayRoundMode(replay, n) === 'per-entry') return turns.length;
  const byChunks = Math.ceil(turns.length / n);
  const maxTurnNumber = turns.reduce((m, t) => Math.max(m, t.turnNumber ?? 0), 0);
  if (maxTurnNumber > 0 && maxTurnNumber <= byChunks) return maxTurnNumber;
  return byChunks;
}

/** Nombre total de lancers de dé enregistrés (tous joueurs confondus). */
export function getReplayDiceRollCount(replay: RaceHistoryReplay, numParticipants: number): number {
  const turns = replay.turns ?? [];
  if (!turns.length) return 0;
  const n = participantCount(numParticipants);
  if (detectReplayRoundMode(replay, n) === 'per-entry') {
    return turns.reduce((sum, t) => sum + rollCountInTurn(t), 0);
  }
  return turns.length;
}

export function clampReplayRound(
  replay: RaceHistoryReplay,
  round: number,
  numParticipants: number
): number {
  const total = getReplayRoundCount(replay, numParticipants);
  if (total <= 0) return 1;
  return Math.max(1, Math.min(total, Math.round(round)));
}

/** Dernier enregistrement chronologique inclus dans le tour de jeu `round`. */
export function lastHistoryStepForRound(
  replay: RaceHistoryReplay,
  round: number,
  numParticipants: number
): number {
  const turns = replay.turns ?? [];
  if (!turns.length) return 0;
  const n = participantCount(numParticipants);
  const r = clampReplayRound(replay, round, numParticipants);
  if (detectReplayRoundMode(replay, n) === 'per-entry') return Math.min(turns.length, r);
  return Math.min(turns.length, r * n);
}

/** Entrée affichée pour le tour (lancers de tous les joueurs — dernière ligne du tour). */
export function getTurnEntryForRound(
  replay: RaceHistoryReplay,
  round: number,
  numParticipants: number
): RaceHistoryTurn | null {
  const step = lastHistoryStepForRound(replay, round, numParticipants);
  if (step <= 0) return null;
  return replay.turns[step - 1] ?? null;
}

/** @deprecated Préférer getReplayRoundCount — conservé pour compatibilité interne. */
export function getReplayTurnCount(replay: RaceHistoryReplay): number {
  return replay.turns?.length ?? 0;
}

export function clampReplayStep(replay: RaceHistoryReplay, step: number): number {
  const total = replay.turns?.length ?? 0;
  if (total <= 0) return 1;
  return Math.max(1, Math.min(total, Math.round(step)));
}

export function getTurnEntryAtStep(
  replay: RaceHistoryReplay,
  step: number
): RaceHistoryTurn | null {
  const total = replay.turns?.length ?? 0;
  if (total <= 0) return null;
  return replay.turns[clampReplayStep(replay, step) - 1] ?? null;
}

function boardAfterFromTurn(turn: RaceHistoryTurn): RaceHistoryPlayerSnapshot[] | undefined {
  const b = turn.boardAfter ?? turn.board;
  return b?.length ? b : undefined;
}

function isMeaningfulSnapshot(
  board: RaceHistoryPlayerSnapshot[] | undefined,
  step: number,
  turn?: RaceHistoryTurn
): boolean {
  if (!board?.length) return false;
  const allAtStart = board.every(
    (s) => s.pathIndex === 0 && !s.finished && (s.lapCount ?? 0) === 0
  );
  if (!allAtStart) return true;
  const hadMove = turn?.rows.some((r) => r.effectiveMove != null && r.effectiveMove > 0);
  return !hadMove && step <= 1;
}

export function resolveBoardBeforeRound(
  replay: RaceHistoryReplay,
  round: number,
  numParticipants: number
): RaceHistoryPlayerSnapshot[] {
  if (round <= 1) {
    const n = participantCount(numParticipants);
    return Array.from({ length: n }, (_, playerIndex) => ({
      playerIndex,
      pathIndex: 0,
      lapCount: 0,
      finished: false,
    }));
  }
  return resolveBoardAfterRound(replay, round - 1, numParticipants);
}

export function resolveBoardAfterRound(
  replay: RaceHistoryReplay,
  round: number,
  numParticipants: number
): RaceHistoryPlayerSnapshot[] {
  const endStep = lastHistoryStepForRound(replay, round, numParticipants);
  if (endStep <= 0) {
    return resolveBoardBeforeRound(replay, 1, numParticipants);
  }
  return resolveBoardAfterHistoryStep(replay, endStep, numParticipants);
}

function resolveBoardAfterHistoryStep(
  replay: RaceHistoryReplay,
  historyStep: number,
  numParticipants: number
): RaceHistoryPlayerSnapshot[] {
  const n = participantCount(numParticipants);
  const entry = getTurnEntryAtStep(replay, historyStep);
  const after = entry ? boardAfterFromTurn(entry) : undefined;
  if (after?.length && isMeaningfulSnapshot(after, historyStep, entry ?? undefined)) {
    return normalizeBoard(after, n);
  }
  return estimateBoardAtHistoryStep(replay, historyStep, n);
}

export function resolveBoardAfterStep(
  replay: RaceHistoryReplay,
  step: number,
  numParticipants: number
): RaceHistoryPlayerSnapshot[] {
  return resolveBoardAfterHistoryStep(replay, step, numParticipants);
}

export function resolveBoardBeforeStep(
  replay: RaceHistoryReplay,
  step: number,
  numParticipants: number
): RaceHistoryPlayerSnapshot[] {
  const n = participantCount(numParticipants);
  if (step <= 1) {
    return Array.from({ length: n }, (_, playerIndex) => ({
      playerIndex,
      pathIndex: 0,
      lapCount: 0,
      finished: false,
    }));
  }
  return resolveBoardAfterHistoryStep(replay, step - 1, numParticipants);
}

export function resolveBoardAtStep(
  replay: RaceHistoryReplay,
  step: number,
  numParticipants: number
): RaceHistoryPlayerSnapshot[] {
  return resolveBoardAfterHistoryStep(replay, step, numParticipants);
}

export function resolveBoardAtTurn(
  replay: RaceHistoryReplay,
  round: number,
  numParticipants: number
): RaceHistoryPlayerSnapshot[] {
  return resolveBoardAfterRound(replay, round, numParticipants);
}

function estimateBoardAtHistoryStep(
  replay: RaceHistoryReplay,
  historyStep: number,
  n: number
): RaceHistoryPlayerSnapshot[] {
  const turns = replay.turns ?? [];
  if (turns.length === 0) {
    return Array.from({ length: n }, (_, playerIndex) => ({
      playerIndex,
      pathIndex: 0,
      lapCount: 0,
      finished: false,
    }));
  }

  const targetIdx = clampReplayStep(replay, historyStep) - 1;
  const pathLen = replayPathLength(replay);
  const pathIndex = Array(n).fill(0);
  const lapCount = Array(n).fill(0);
  const finished = Array(n).fill(false);
  let lastExact: RaceHistoryPlayerSnapshot[] | null = null;

  for (let i = 0; i <= targetIdx; i++) {
    const turn = turns[i];
    const snap = boardAfterFromTurn(turn);
    if (snap?.length && isMeaningfulSnapshot(snap, i + 1, turn)) {
      lastExact = snap.map((s) => ({ ...s }));
      for (const s of lastExact) {
        const pi = s.playerIndex;
        if (pi >= 0 && pi < n) {
          pathIndex[pi] = s.pathIndex;
          lapCount[pi] = s.lapCount;
          finished[pi] = s.finished;
        }
      }
    } else if (pathLen > 0) {
      for (const mover of resolveMovesForHistoryEntry(turn, i, n)) {
        const pi = mover.playerIndex;
        const prev = pathIndex[pi] ?? 0;
        const next = (prev + mover.effectiveMove) % pathLen;
        if (next < prev) lapCount[pi] = (lapCount[pi] ?? 0) + 1;
        pathIndex[pi] = next;
      }
    }

    if (i === targetIdx) {
      if (snap?.length && isMeaningfulSnapshot(snap, historyStep, turn)) {
        return normalizeBoard(snap, n);
      }
      if (lastExact) return normalizeBoard(lastExact, n);
      return Array.from({ length: n }, (_, playerIndex) => ({
        playerIndex,
        pathIndex: pathIndex[playerIndex] ?? 0,
        lapCount: lapCount[playerIndex] ?? 0,
        finished: finished[playerIndex] ?? false,
      }));
    }
  }

  if (lastExact) return normalizeBoard(lastExact, n);
  return Array.from({ length: n }, (_, playerIndex) => ({
    playerIndex,
    pathIndex: pathIndex[playerIndex] ?? 0,
    lapCount: lapCount[playerIndex] ?? 0,
    finished: finished[playerIndex] ?? false,
  }));
}

/** Mouvements à appliquer pour une entrée d’historique (un ou plusieurs lancers). */
function resolveMovesForHistoryEntry(
  turn: RaceHistoryTurn,
  entryIndex: number,
  n: number
): { playerIndex: number; effectiveMove: number }[] {
  if (turn.actorPlayerIndex != null) {
    const row = turn.rows.find((r) => r.playerIndex === turn.actorPlayerIndex);
    if (row?.effectiveMove != null && row.effectiveMove > 0) {
      return [{ playerIndex: turn.actorPlayerIndex, effectiveMove: row.effectiveMove }];
    }
  }
  const withRoll = turn.rows.filter(
    (r) => r.roll != null && r.roll > 0 && r.effectiveMove != null && r.effectiveMove > 0
  );
  if (withRoll.length > 1) {
    return withRoll.map((r) => ({
      playerIndex: r.playerIndex,
      effectiveMove: r.effectiveMove!,
    }));
  }
  if (withRoll.length === 1) {
    const r = withRoll[0];
    return [{ playerIndex: r.playerIndex, effectiveMove: r.effectiveMove! }];
  }
  const actor = entryIndex % n;
  const fallback = turn.rows.find((r) => r.playerIndex === actor);
  if (fallback?.effectiveMove != null && fallback.effectiveMove > 0) {
    return [{ playerIndex: actor, effectiveMove: fallback.effectiveMove }];
  }
  return [];
}

function resolveActorMoveForHistoryEntry(
  turn: RaceHistoryTurn,
  entryIndex: number,
  n: number
): { playerIndex: number; effectiveMove: number } | null {
  const moves = resolveMovesForHistoryEntry(turn, entryIndex, n);
  return moves[0] ?? null;
}

/** Lignes pilotes / dés pour tout le tour (fusion des entrées chronologiques du tour). */
export function resolveRowsForRound(
  replay: RaceHistoryReplay,
  round: number,
  numParticipants: number
): RaceHistoryTurn['rows'] {
  const turns = replay.turns ?? [];
  const n = participantCount(numParticipants);
  const r = clampReplayRound(replay, round, numParticipants);
  const end = lastHistoryStepForRound(replay, r, n);
  const mode = detectReplayRoundMode(replay, n);
  const start = mode === 'per-entry' ? Math.max(0, r - 1) : Math.max(0, (r - 1) * n);
  const byPlayer = new Map<number, RaceHistoryTurn['rows'][number]>();
  for (let i = start; i < end; i++) {
    const entry = turns[i];
    if (!entry?.rows.length) continue;
    for (const row of entry.rows) {
      if (row.roll != null && row.roll > 0) byPlayer.set(row.playerIndex, row);
    }
  }
  const last = getTurnEntryForRound(replay, r, n);
  if (last?.rows.length) {
    for (const row of last.rows) {
      if (!byPlayer.has(row.playerIndex)) byPlayer.set(row.playerIndex, row);
    }
  }
  const rows = [...byPlayer.values()];
  if (last?.turnOrderAtRoundStart?.length) {
    const ordered: RaceHistoryTurn['rows'] = [];
    const seen = new Set<number>();
    for (const playerIndex of last.turnOrderAtRoundStart) {
      const row = byPlayer.get(playerIndex);
      if (row) {
        ordered.push(row);
        seen.add(playerIndex);
      }
    }
    for (const row of rows) {
      if (!seen.has(row.playerIndex)) ordered.push(row);
    }
    return ordered;
  }
  return rows.sort((a, b) => a.rank - b.rank);
}

/** Tous les lancers du tour de jeu (ligne par joueur sur la dernière entrée du tour). */
export function diceMovesForRound(
  replay: RaceHistoryReplay,
  round: number,
  numParticipants: number
): { playerIndex: number; effectiveMove: number }[] {
  const entry = getTurnEntryForRound(replay, round, numParticipants);
  if (!entry?.rows.length) return [];
  return [...entry.rows]
    .filter((r) => r.roll != null && r.roll > 0 && r.effectiveMove != null && r.effectiveMove > 0)
    .sort((a, b) => a.rank - b.rank)
    .map((r) => ({ playerIndex: r.playerIndex, effectiveMove: r.effectiveMove! }));
}

export function diceMovesForStep(
  replay: RaceHistoryReplay,
  step: number,
  participantCount = 2
): { playerIndex: number; effectiveMove: number }[] {
  const entry = getTurnEntryAtStep(replay, step);
  if (!entry?.rows.length) return [];
  const mover = resolveActorMoveForHistoryEntry(
    entry,
    clampReplayStep(replay, step) - 1,
    participantCount
  );
  return mover ? [mover] : [];
}

export function diceMovesForTurn(
  replay: RaceHistoryReplay,
  turnNumber: number
): { playerIndex: number; effectiveMove: number }[] {
  return diceMovesForStep(replay, turnNumber);
}

function rankSnapshotsFromBoard(
  board: RaceHistoryPlayerSnapshot[],
  circuit?: Circuit
): { playerIndex: number; rank: number }[] {
  const entries = board.map((s) => {
    const entry = buildRaceRankingEntry(circuit, s.playerIndex, {
      finished: s.finished,
      finishStamp: 0,
      lapCount: s.lapCount,
      mainPathIndex: s.pathIndex,
      jokerBranchIndex: s.jokerBranchIndex ?? null,
      startLaneIndex: s.startLaneIndex ?? null,
      totalDiceRolls: 0,
    });
    return {
      ...entry,
      progressScore: s.progressScore ?? entry.progressScore,
    };
  });
  return buildRaceRanking(entries);
}

export function rankingAtRound(
  replay: RaceHistoryReplay,
  round: number,
  numParticipants: number
): { playerIndex: number; rank: number }[] {
  const entry = getTurnEntryForRound(replay, round, numParticipants);
  if (entry?.rows.length) {
    return [...entry.rows]
      .sort((a, b) => a.rank - b.rank)
      .map((r) => ({ playerIndex: r.playerIndex, rank: r.rank }));
  }
  const board = resolveBoardAfterRound(replay, round, numParticipants);
  return rankSnapshotsFromBoard(board, replay.circuit);
}

export function rankingAtStep(
  replay: RaceHistoryReplay,
  step: number,
  participantCount: number
): { playerIndex: number; rank: number }[] {
  const entry = getTurnEntryAtStep(replay, step);
  if (entry?.rows.length) {
    return [...entry.rows]
      .sort((a, b) => a.rank - b.rank)
      .map((r) => ({ playerIndex: r.playerIndex, rank: r.rank }));
  }
  const board = resolveBoardAfterHistoryStep(replay, step, participantCount);
  return rankSnapshotsFromBoard(board, replay.circuit);
}

export function rankingAtTurn(
  replay: RaceHistoryReplay,
  turnNumber: number,
  participantCount: number
): { playerIndex: number; rank: number }[] {
  return rankingAtRound(replay, turnNumber, participantCount);
}

function normalizeBoard(
  board: RaceHistoryPlayerSnapshot[],
  n: number
): RaceHistoryPlayerSnapshot[] {
  const byIndex = new Map(board.map((s) => [s.playerIndex, s]));
  return Array.from({ length: n }, (_, playerIndex) => {
    const s = byIndex.get(playerIndex);
    return {
      playerIndex,
      pathIndex: s?.pathIndex ?? 0,
      lapCount: s?.lapCount ?? 0,
      finished: s?.finished ?? false,
      jokerBranchIndex: s?.jokerBranchIndex ?? null,
      startLaneIndex: s?.startLaneIndex ?? null,
      jokerLapUsed: s?.jokerLapUsed ?? false,
      progressScore: s?.progressScore,
    };
  });
}
